// app/src/modules/ui/Loader.tsx — shared loading UI used across the whole app:
// a global top progress bar (driven by in-flight API requests), spinners and
// skeleton placeholders. Replaces the old ad-hoc static “Loading…” text.
import { useEffect, useRef, useState } from "react";

/* Circular spinner. Uses the global .spin keyframe (styles/global.css). */
export function Spinner({ size = 22, stroke = 2.5, color = "var(--accent)" }: { size?: number; stroke?: number; color?: string }) {
  return (
    <span
      className="spin"
      aria-hidden
      style={{ display: "inline-block", width: size, height: size, borderRadius: "50%", border: `${stroke}px solid var(--line)`, borderTopColor: color, flexShrink: 0 }}
    />
  );
}

/* Small light spinner for use inside buttons:
   <button>{busy ? <><InlineSpinner/> Saving…</> : "Save"}</button> */
export function InlineSpinner({ size = 14, color = "#fff" }: { size?: number; color?: string }) {
  return (
    <span
      className="spin"
      aria-hidden
      style={{ display: "inline-block", width: size, height: size, borderRadius: "50%", border: "2.5px solid rgba(255,255,255,.45)", borderTopColor: color, verticalAlign: "-2px", marginRight: 6 }}
    />
  );
}

/* Centered spinner + label — drop-in replacement for static “Loading…” text. */
export function Loading({ label = "Loading…", pad = 40 }: { label?: string; pad?: number }) {
  return (
    <div role="status" aria-live="polite" style={{ display: "grid", placeItems: "center", gap: 10, padding: pad, color: "var(--muted)", width: "100%" }}>
      <Spinner />
      {label ? <span className="t-13">{label}</span> : null}
    </div>
  );
}

/* A single shimmer placeholder block. */
export function Skeleton({ w = "100%", h = 14, r, style }: { w?: number | string; h?: number | string; r?: number | string; style?: React.CSSProperties }) {
  return <div className="skeleton" style={{ width: w, height: h, borderRadius: r ?? "var(--r-sm)", ...style }} />;
}

/* Skeleton rows for a table <tbody>. `cols` = number of columns per row. */
export function TableSkeleton({ rows = 6, cols }: { rows?: number; cols: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i}>
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j}><div className="skeleton" style={{ height: 14, borderRadius: 4 }} /></td>
          ))}
        </tr>
      ))}
    </>
  );
}

/* Skeleton cards for a grid/list of cards. */
export function CardSkeleton({ count = 6, height = 90, radius = "var(--r-md)" }: { count?: number; height?: number; radius?: string }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skeleton" style={{ height, borderRadius: radius }} />
      ))}
    </>
  );
}

/* Global top progress bar — appears whenever any API request is in flight.
   Mounted once in App. Listens for the “epos:loading” events emitted by the
   api layer; a short delay avoids flashing on very fast requests. */
export function TopProgressBar() {
  const [on, setOn] = useState(false);
  const shown = useRef(false);
  const showT = useRef<number | undefined>(undefined);
  const hideT = useRef<number | undefined>(undefined);

  useEffect(() => {
    const handle = (e: Event) => {
      const active = (e as CustomEvent<number>).detail ?? 0;
      if (active > 0) {
        if (hideT.current !== undefined) { window.clearTimeout(hideT.current); hideT.current = undefined; }
        if (!shown.current && showT.current === undefined) {
          showT.current = window.setTimeout(() => { setOn(true); shown.current = true; showT.current = undefined; }, 120);
        }
      } else {
        if (showT.current !== undefined) { window.clearTimeout(showT.current); showT.current = undefined; }
        if (shown.current && hideT.current === undefined) {
          hideT.current = window.setTimeout(() => { setOn(false); shown.current = false; hideT.current = undefined; }, 220);
        }
      }
    };
    window.addEventListener("epos:loading", handle as EventListener);
    return () => {
      window.removeEventListener("epos:loading", handle as EventListener);
      window.clearTimeout(showT.current); window.clearTimeout(hideT.current);
    };
  }, []);

  if (!on) return null;
  return <div className="epos-topbar" aria-hidden><span /></div>;
}

/* Empty state for lists/data panels: concise title, optional supporting line, optional action.
 * Use for "loaded with zero rows" and "filtered with zero matches" — never while still loading.
 * role="status" so assistive tech announces it without stealing focus. Tokens → light/dark safe. */
export function EmptyState({ title, hint, action, icon, pad = 48 }: { title: string; hint?: string; action?: React.ReactNode; icon?: React.ReactNode; pad?: number }) {
  return (
    <div role="status" style={{ display: "grid", justifyItems: "center", textAlign: "center", gap: 8, padding: pad }}>
      {icon != null && <div aria-hidden="true" style={{ color: "var(--muted)", opacity: 0.75 }}>{icon}</div>}
      <div style={{ fontWeight: 700, fontSize: 15, color: "var(--ink-2)" }}>{title}</div>
      {hint && <div style={{ fontSize: 13, color: "var(--muted)", maxWidth: 360, lineHeight: 1.5 }}>{hint}</div>}
      {action != null && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  );
}

/* Error state for a failed fetch: concise message + optional Retry (render the button only when a
 * retry function already exists — never invent one). role="alert" announces the failure. */
export function ErrorState({ title = "Couldn’t load this", detail, onRetry, retryLabel = "Try again", pad = 48 }: { title?: string; detail?: string; onRetry?: () => void; retryLabel?: string; pad?: number }) {
  return (
    <div role="alert" style={{ display: "grid", justifyItems: "center", textAlign: "center", gap: 8, padding: pad }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: "var(--ink)" }}>{title}</div>
      {detail && <div style={{ fontSize: 13, color: "var(--muted)", maxWidth: 380, lineHeight: 1.5 }}>{detail}</div>}
      {onRetry && (
        <button onClick={onRetry} style={{ marginTop: 8, height: "var(--h-md)", padding: "0 16px", borderRadius: "var(--r-sm)", border: "1.5px solid var(--line-strong)", background: "var(--card)", color: "var(--ink)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>{retryLabel}</button>
      )}
    </div>
  );
}
